import { ReactNode, useCallback } from "react";
import * as React from "react";
import Draggable from "react-draggable";
import { FieldValues, UseFormStateProps } from "react-hook-form";

import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper, { PaperProps } from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Breakpoint, SxProps, useTheme } from "@mui/material/styles";

import SupaDemoButton from "../supa-demo-button";
import focusFormField from "./focus-form-field.util";
import { Blocker, LiveFormBlockers } from "./form-blockers.component";
import SubmitButton from "./submit-button.component";

type ModalFormProps = {
  submitButtonLabel?: ReactNode;
  isOpen: boolean;
  submitIsDisabled?: boolean;
  submitTooltip?: string;
  showCancelButton?: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit?: () => void;
  formName?: string;
  title?: string;
  children?: ReactNode;
  hideBackdrop?: boolean;
  maxWidth?: false | Breakpoint;
  dialogActionsSx?: SxProps;
  dialogContentSx?: SxProps;
  dialogTitleSx?: SxProps;
  showBackground?: boolean;
  sx?: SxProps;
  zIndex?: number;
  closeOnBackdropClick?: boolean;
  submitButtonSx?: SxProps;
  supaDemoId?: string;
  actionsChildren?: ReactNode;
  autoFocusContent?: boolean;
};

export type RhfModalFormProps<T extends FieldValues> = ModalFormProps &
  UseFormStateProps<T> & {
    /**
     * Opt in to DEV-2995's blockers treatment: renders a strip above the actions naming
     * what is unmet, and stops disabling Save on validity so the click can reveal the
     * errors. Requires `onSubmit` to be a `handleSubmit(...)` wrapper — that is what
     * refuses to submit an invalid form. Omit entirely to keep disable-on-invalid.
     *
     * A function of the form's values, not a list, so that only the strip watches the
     * form. Keep it stable (useCallback/useMemo) — a new function re-runs it.
     */
    getBlockers?: (values: T) => Blocker[];
  };

export default function ModalForm<T extends FieldValues>({
  isOpen,
  submitButtonLabel = "Save",
  submitIsDisabled,
  submitTooltip,
  showCancelButton = false,
  isSubmitting = false,
  onCancel,
  onSubmit,
  title = "",
  formName = "title",
  hideBackdrop = true,
  maxWidth = "sm",
  control,
  children,
  dialogActionsSx,
  dialogContentSx,
  dialogTitleSx,
  showBackground,
  submitButtonSx,
  zIndex,
  closeOnBackdropClick = false,
  supaDemoId,
  actionsChildren,
  autoFocusContent = false,
  getBlockers,
  sx
}: RhfModalFormProps<T>) {
  const { palette } = useTheme();
  const nodeRef = React.useRef(null);
  const latestBlockers = React.useRef<Blocker[]>([]);

  const PaperComponent = useCallback(
    (props: PaperProps) => {
      return (
        <Draggable
          handle={`#draggable-dialog-${formName}`}
          cancel={'[class*="MuiDialogContent-root"]'}
          nodeRef={nodeRef}
        >
          <Paper {...props} elevation={1} ref={nodeRef} />
        </Draggable>
      );
    },
    [formName]
  );

  const onClose = useCallback(
    (event: object, reason: string) => {
      if (isSubmitting) {
        return; // Prevent closing while submitting
      }
      if (reason !== "backdropClick" || closeOnBackdropClick) {
        onCancel();
      }
    },
    [closeOnBackdropClick, onCancel, isSubmitting]
  );
  // Pressing Save on a blocked form should land the user on the problem, not just
  // paint it red somewhere off-screen. RHF's own shouldFocusError cannot be relied on
  // here: these fields are Controller-based and spread {...field} onto MUI Select, so
  // the ref it focuses may not be focusable. Jump to the first blocker that resolves.
  const submitAndReveal = () => {
    onSubmit?.();

    latestBlockers.current.some((blocker) => focusFormField(blocker.name));
  };

  const actions = (
    <>
      {actionsChildren}
      {showCancelButton && (
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      )}
      {onSubmit && (
        <SubmitButton
          submitButtonLabel={submitButtonLabel}
          formName={formName}
          submitIsDisabled={submitIsDisabled}
          submitTooltip={submitTooltip}
          isSubmitting={isSubmitting}
          onSubmit={getBlockers ? submitAndReveal : onSubmit}
          control={control}
          hasBlockers={Boolean(getBlockers)}
          sx={submitButtonSx}
        />
      )}
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperComponent={PaperComponent}
      fullWidth
      maxWidth={maxWidth}
      hideBackdrop={hideBackdrop}
      data-testid={`draggable-dialog-${formName}`}
      aria-labelledby={`draggable-dialog-${formName}`}
      PaperProps={{
        sx: sx,
        style: hideBackdrop ? { pointerEvents: "auto" } : undefined
      }}
      className="animate__animated animate__fadeIn animate__faster"
      disableAutoFocus={autoFocusContent}
      disableEnforceFocus={hideBackdrop}
      disableEscapeKeyDown={isSubmitting}
      style={hideBackdrop ? { pointerEvents: "none" } : undefined}
      sx={{
        backgroundColor: showBackground ? "rgba(0,0,0,0.5)" : undefined,
        zIndex
      }}
    >
      <form id={formName}>
        <DialogTitle
          id={`draggable-dialog-${formName}`}
          sx={{
            cursor: "move",
            p: 1,
            bgcolor: palette.lightBackground,
            ...dialogTitleSx
          }}
        >
          <Stack
            flexDirection="row"
            alignContent="center"
            justifyContent="space-between"
            width="100%"
          >
            <Stack flexDirection="row" justifyContent="space-between">
              <IconButton>
                <DragIndicatorIcon />
              </IconButton>
              <Box display="flex" alignItems="center">
                <Typography variant="h5" component="div">
                  {title}
                </Typography>
              </Box>
              {supaDemoId && (
                <SupaDemoButton supaDemoId={supaDemoId} sx={{ ml: 2 }} />
              )}
            </Stack>
            <IconButton
              onClick={isSubmitting ? undefined : onCancel}
              onTouchStart={isSubmitting ? undefined : onCancel}
              disabled={isSubmitting}
              data-testid="close-modal-button"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pb: 1, ...dialogContentSx }}>
          {children}
        </DialogContent>
        {getBlockers ? (
          // The strip shares the sticky footer with the actions it explains. A sticky
          // element cannot float past its own parent's box, so a strip pinned on its own
          // only rises by the height of whatever follows it — it would sit at the bottom
          // of a long form while Save, pinned separately, is all the user can see.
          // Wrapping both as the last child of the form is what keeps them together, and
          // the flex row keeps them side by side while there is width for it
          // (both found in DEV-2995 browser verification).
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              px: 2,
              pb: 1,
              bgcolor: palette.lightBackground,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
              <LiveFormBlockers
                control={control}
                getBlockers={getBlockers}
                latestBlockers={latestBlockers}
                onJump={focusFormField}
              />
            </Box>
            <DialogActions
              // A consumer's own sx may pin the actions separately (the appointment form
              // makes them sticky with a 10px offset), which would sit them out of line
              // with the strip now that the wrapper above does the pinning.
              sx={[
                ...(Array.isArray(dialogActionsSx)
                  ? dialogActionsSx
                  : [dialogActionsSx]),
                { position: "static", flexShrink: 0, ml: "auto", py: 0 }
              ]}
            >
              {actions}
            </DialogActions>
          </Box>
        ) : (
          <DialogActions sx={dialogActionsSx}>{actions}</DialogActions>
        )}
      </form>
    </Dialog>
  );
}
